import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Tabs,
  Tab,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Home as PropertyIcon,
  TrendingUp as PricingIcon,
  LocationOn as NeighborhoodIcon,
  Balance as NegotiationIcon,
  Architecture as FloorPlanIcon,
  Analytics as CMAIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, selectAnalysisResults, setResultsTabOpen } from '../../store';
import { PropertyAnalysisData } from '../../types/property';

// Import tab components (will be created next)
import PropertyDataTab from './analysis-tabs/PropertyDataTab';
import PricingTab from './analysis-tabs/PricingTab';
import CMATab from './analysis-tabs/CMATab';
import NeighborhoodTab from './analysis-tabs/NeighborhoodTab';
import NegotiationTab from './analysis-tabs/NegotiationTab';
import FloorPlanTab from './analysis-tabs/FloorPlanTab';
import ListingDescriptionTab from './analysis-tabs/ListingDescriptionTab';
import ClientConsultationTab from './analysis-tabs/ClientConsultationTab';

interface PropertyAnalysisResultsProps {
  open: boolean;
  onClose: () => void;
  onShare: () => void;
  onExport: () => void;
}

interface TabData {
  id: string;
  label: string;
  icon: React.ReactNode;
  component: React.ComponentType<{ data: any; loading?: boolean; error?: string; }>;
  dataKey: keyof PropertyAnalysisData['aiAnalysis'];
  description: string;
}

const PropertyAnalysisResults: React.FC<PropertyAnalysisResultsProps> = ({
  open,
  onClose,
  onShare,
  onExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  
  const results = useAppSelector(selectAnalysisResults);
  const [activeTab, setActiveTab] = useState('property');
  
  // Update active tab when analysis data changes
  useEffect(() => {
    if (results?.aiAnalysis) {
      // Check if property tab is available, if not use the first available
      if (results.aiAnalysis.propertyParser) {
        setActiveTab('property');
      } else {
        // Find first available tab
        const availableKeys = Object.keys(results.aiAnalysis);
        if (availableKeys.length > 0) {
          // Map analysis keys to tab IDs
          const keyToTabMap: Record<string, string> = {
            'propertyParser': 'property',
            'pricing': 'pricing',
            'cma': 'cma',
            'neighborhood': 'neighborhood',
            'negotiation': 'negotiation',
            'floorPlan': 'floorplan',
            'listingDescription': 'listing-description',
            'clientConsultation': 'client-consultation',
          };
          const firstAvailableTab = keyToTabMap[availableKeys[0]] || 'property';
          setActiveTab(firstAvailableTab);
        }
      }
    }
  }, [results?.aiAnalysis]);
  
  // Early return if no results to prevent unnecessary rendering and debugging
  if (!results) {
    return null;
  }
  
  // Filter tabs based on available data - simple filtering without memoization
  const availableTabs: TabData[] = [];
  if (results?.aiAnalysis) {
    if (results.aiAnalysis.propertyParser) {
      availableTabs.push({
        id: 'property',
        label: 'Property Data',
        icon: <PropertyIcon />,
        component: PropertyDataTab,
        dataKey: 'propertyParser' as const,
        description: 'Basic property information and specifications',
      });
    }
    if (results.aiAnalysis.pricing) {
      availableTabs.push({
        id: 'pricing',
        label: 'Pricing Strategy',
        icon: <PricingIcon />,
        component: PricingTab,
        dataKey: 'pricing' as const,
        description: 'Market positioning and pricing recommendations',
      });
    }
    if (results.aiAnalysis.cma) {
      availableTabs.push({
        id: 'cma',
        label: 'Market Analysis',
        icon: <CMAIcon />,
        component: CMATab,
        dataKey: 'cma' as const,
        description: 'Comparative market analysis and trends',
      });
    }
    if (results.aiAnalysis.neighborhood) {
      availableTabs.push({
        id: 'neighborhood',
        label: 'Neighborhood',
        icon: <NeighborhoodIcon />,
        component: NeighborhoodTab,
        dataKey: 'neighborhood' as const,
        description: 'Local area insights and demographics',
      });
    }
    if (results.aiAnalysis.negotiation) {
      availableTabs.push({
        id: 'negotiation',
        label: 'Negotiation',
        icon: <NegotiationIcon />,
        component: NegotiationTab,
        dataKey: 'negotiation' as const,
        description: 'Negotiation strategies and recommendations',
      });
    }
    if (results.aiAnalysis.floorPlan) {
      availableTabs.push({
        id: 'floorplan',
        label: 'Floor Plan',
        icon: <FloorPlanIcon />,
        component: FloorPlanTab,
        dataKey: 'floorPlan' as const,
        description: 'Layout analysis and space utilization',
      });
    }
    if (results.aiAnalysis.listingDescription) {
      availableTabs.push({
        id: 'listing-description',
        label: 'Listing Description',
        icon: <PropertyIcon />,
        component: ListingDescriptionTab,
        dataKey: 'listingDescription' as const,
        description: 'Detailed analysis of the property listing description',
      });
    }
    if (results.aiAnalysis.clientConsultation) {
      availableTabs.push({
        id: 'client-consultation',
        label: 'Client Consultation',
        icon: <PropertyIcon />,
        component: ClientConsultationTab,
        dataKey: 'clientConsultation' as const,
        description: 'Detailed analysis of client consultation notes',
      });
    }
  }


  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    dispatch(setResultsTabOpen(newValue));
  };

  // Get active tab data
  const activeTabData = (() => {
    const tab = availableTabs.find(t => t.id === activeTab);
    if (!tab || !results) return null;
    
    // For PropertyDataTab, use the properly extracted data instead of the AI analysis
    if (tab.id === 'property' && results.property?.extractionData?.property) {
      return {
        tab,
        data: {
          property: results.property.extractionData.property,
          verification: results.property.extractionData.verification || results.aiAnalysis.propertyParser?.verification,
        },
      };
    }
    
    const tabData = results.aiAnalysis?.[tab.dataKey];
    if (!tabData) return null;
    
    return {
      tab,
      data: tabData,
    };
  })();

  // Get property summary info
  const propertyData = results.property?.extractionData?.property || {};
  const summary = results.summary || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            height: isMobile ? '100vh' : '90vh',
            maxHeight: isMobile ? '100vh' : '90vh',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PropertyIcon color="primary" />
            <Typography variant="h6">
              Property Analysis Results
            </Typography>
            <Chip 
              label={`Confidence: ${Math.round((summary.confidence || 0) * 100)}%`}
              color={summary.confidence > 0.8 ? 'success' : summary.confidence > 0.6 ? 'warning' : 'error'}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {propertyData.address || results.property?.url || 'Property Analysis'}
          </Typography>
          {summary.recommendedPrice && (
            <Typography variant="body2" color="primary" fontWeight="bold">
              Recommended Price: ${summary.recommendedPrice.toLocaleString()}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onShare} size="small" title="Share Analysis">
            <ShareIcon />
          </IconButton>
          <IconButton onClick={onExport} size="small" title="Export Results">
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small" title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100%' }}>
        {/* Tabs Navigation */}
        <Box 
          sx={{ 
            width: isMobile ? '100%' : 280, 
            borderRight: isMobile ? 'none' : 1, 
            borderColor: 'divider',
            borderBottom: isMobile ? 1 : 'none',
          }}
        >
          <Tabs
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-flexContainer': {
                alignItems: isMobile ? 'center' : 'flex-start',
              },
              '& .MuiTab-root': {
                minHeight: isMobile ? 48 : 72,
                textAlign: 'left',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
              },
            }}
          >
            {availableTabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {tab.icon}
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {tab.label}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {tab.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                }
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <DialogContent sx={{ flex: 1, p: 0 }}>
            {activeTabData ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Tab Header */}
                <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {activeTabData.tab.icon}
                    <Typography variant="h6">
                      {activeTabData.tab.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {activeTabData.tab.description}
                  </Typography>
                </Box>

                {/* Tab Component */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                  <activeTabData.tab.component 
                    data={activeTabData.data}
                    loading={false}
                    error={undefined}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                flexDirection: 'column',
                gap: 2,
              }}>
                <Typography variant="h6" color="text.secondary">
                  No Data Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This analysis section doesn't contain any data.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Box>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button 
            startIcon={<BookmarkIcon />}
            variant="outlined" 
            onClick={() => {/* TODO: Save to favorites */}}
          >
            Save Analysis
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<ShareIcon />} variant="outlined" onClick={onShare}>
            Share
          </Button>
          <Button startIcon={<DownloadIcon />} variant="contained" onClick={onExport}>
            Export
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyAnalysisResults;